import { scriptImported, SCRIPT_IMPORTED, toggleInterstitialAd } from '../store/modules/adnimation'
import { ROUTE_FOUND } from '../store/modules/router'
import { combineEpics, ofType } from 'redux-observable'
import { ignoreElements, map, mergeMap, tap, take, filter, withLatestFrom } from 'rxjs'
import { logObservableError } from '../utilities/logs'
import { TCDATA_READY } from '../store/modules/consentManagement'
import { ARTICLE_RECEIVED } from '../store/modules/article'
import { RECEIVED as CATEGORY_RECEIVED } from '../store/modules/category'
import { isArticleRoute, isCategoryRoute } from '../utilities/urls'

// getAdContext :: Object -> Object
const getAdContext = state => {
  const activeRouteName = state.router.activeRoute.name

  const articleRouteDetected = isArticleRoute(activeRouteName)
  const categoryRouteDetected = isCategoryRoute(activeRouteName)

  const disabledAds =
    articleRouteDetected
      ? state.article?.content?.disabledAdnimationAds ?? false
      : categoryRouteDetected
        ? state.category?.category?.disabledAds ?? false
        : false

  return {
    articleRouteDetected,
    categoryRouteDetected,
    disabledAds,
  }
}

// shouldSkipOnRouteChange :: (Object, Object) -> Boolean
const shouldSkipOnRouteChange = (action, adContext) =>
  (action.type === SCRIPT_IMPORTED || action.type === ROUTE_FOUND)
  && (adContext.articleRouteDetected || adContext.categoryRouteDetected)

// The Adnimation script constantly redefines or pushes functions
// into `window.googletag.cmd`, even after we clear it. A single reset like
// `window.googletag.cmd = []` is not enough so the script will immediately
// repopulate it. Using a timer ensures that every ~200ms we recheck and clear
// the array if needed, keeping it truly empty while ads are disabled.
const startGoogletagCmdGuard = window => {
  if (window.__adnimationGoogletagGuard) return

  window.__adnimationGoogletagGuard = window.setInterval(() => {
    if (!window.googletag) {
      window.googletag = { cmd: [] }
      return
    }

    if (!Array.isArray(window.googletag.cmd)) {
      window.googletag.cmd = []
      return
    }

    window.googletag.cmd.length = 0
  }, 200)
}

// Stops the guard once ads are enabled again,
// allowing the Adnimation script to freely manage googletag.cmd.
const stopGoogletagCmdGuard = window => {
  if (!window.__adnimationGoogletagGuard) return

  window.clearInterval(window.__adnimationGoogletagGuard)
  window.__adnimationGoogletagGuard = null
}

// ensureGuardForState :: (Object, Object) -> _
const ensureGuardForState = (state, window) =>
  getAdContext(state).disabledAds
    ? startGoogletagCmdGuard(window)
    : stopGoogletagCmdGuard(window)

// importScriptEpic :: Epic -> Observable Action.SCRIPT_IMPORTED
export const importScriptEpic = (action$, state$, { document, logger, window }) =>
  action$.pipe(
    ofType(TCDATA_READY),
    withLatestFrom(state$),
    take(1),
    mergeMap(([ _, initialState ]) =>
      new Promise(resolve => {
        ensureGuardForState(initialState, window)
        const script = document.createElement('script')
        script.async = true
        script.src = 'https://adncdn.net/5eyDSZ2YeO'
        script.onload = () => {
          ensureGuardForState(state$.value, window)
          resolve()
        }
        document.head.appendChild(script)
      }),
    ),
    map(scriptImported),
    logObservableError('adnimation :: importScriptEpic', logger),
  )

// This epic is required because `ensureGuardForState` is only called inside `importScriptEpic`
// during the first script load. Once the Adnimation script is imported, that epic no longer runs,
// meaning changes in page context (e.g., navigating from a category with ads enabled
// to one with ads disabled) would never re-trigger the googletag guard.
// By listening to ROUTE_FOUND, CATEGORY_RECEIVED, and ARTICLE_RECEIVED actions,
// this epic continuously re-syncs the googletag guard state (start/stop) after navigation,
// ensuring that ads are correctly enabled or blocked even after the script has already loaded.
//
// syncGoogletagGuardEpic :: Epic -> _
export const syncGoogletagGuardEpic = (action$, state$, { window, logger }) =>
  action$.pipe(
    ofType(ROUTE_FOUND, CATEGORY_RECEIVED, ARTICLE_RECEIVED),
    withLatestFrom(state$),
    filter(([ _, state ]) => state.adnimation.scriptImported),
    tap(([ _, state ]) => ensureGuardForState(state, window)),
    ignoreElements(),
    logObservableError('adnimation :: syncGoogletagGuardEpic', logger),
  )

// toggleOnInterstitialAdEpic :: Epic -> Observable Action.TOGGLE_INTERSTITIAL_AD
export const toggleOnInterstitialAdEpic = (action$, state$, { window, logger }) =>
  action$.pipe(
    ofType(SCRIPT_IMPORTED, ROUTE_FOUND, CATEGORY_RECEIVED, ARTICLE_RECEIVED),
    withLatestFrom(state$),
    filter(([ action, state ]) => {
      const adContext = getAdContext(state)
      return (
        state.adnimation.scriptImported
        && !state.adnimation.displayInterstitialAd
        && !shouldSkipOnRouteChange(action, adContext)
        && !adContext.disabledAds
      )
    }),
    tap(([ _, state ]) => {
      stopGoogletagCmdGuard(window)

      window.googletag = window.googletag || { cmd: [] }
      window.googletag.cmd = Array.isArray(window.googletag.cmd)
        ? window.googletag.cmd
        : []

      window.googletag.cmd.push(function () {
        const slot = window.googletag.defineOutOfPageSlot(
          `/22034362842/i24Web/i24web_interstitial_${state.router.locale}`,
          window.googletag.enums.OutOfPageFormat.INTERSTITIAL,
        )
        if (slot) {
          slot.addService(window.googletag.pubads()).setConfig({
            interstitial: { triggers: { navBar: true, unhideWindow: true } },
          })
          window.googletag.enableServices()
          window.googletag.display(slot)
        }
      })
    }),
    map(toggleInterstitialAd),
    logObservableError('adnimation :: toggleOnInterstitialAdEpic', logger),
  )

// toggleOffInterstitialAdEpic :: Epic -> Observable Action.TOGGLE_INTERSTITIAL_AD
export const toggleOffInterstitialAdEpic = (action$, state$, { window, logger }) =>
  action$.pipe(
    ofType(CATEGORY_RECEIVED, ARTICLE_RECEIVED),
    withLatestFrom(state$),
    filter(([ _, state ]) => {
      const { disabledAds } = getAdContext(state)
      return (
        state.adnimation.scriptImported
        && state.adnimation.displayInterstitialAd
        && disabledAds
      )
    }),
    tap(() => startGoogletagCmdGuard(window)),
    map(toggleInterstitialAd),
    logObservableError('adnimation :: toggleOffInterstitialAdEpic', logger),
  )

// stickyFooterAdEpic :: Epic -> _
export const stickyFooterAdEpic = (action$, state$, { window, logger }) =>
  action$.pipe(
    ofType(ROUTE_FOUND, CATEGORY_RECEIVED, ARTICLE_RECEIVED),
    withLatestFrom(state$),
    filter(([ action, state ]) => {
      const adContext = getAdContext(state)
      return (
        state.adnimation.scriptImported
        && state.router.matchesCounter > 1
        && !shouldSkipOnRouteChange(action, adContext)
        && !adContext.disabledAds
        && typeof window?.adn?.createStickyBanner === 'function'
      )
    }),
    tap(() => window.adn.createStickyBanner()),
    ignoreElements(),
    logObservableError('adnimation :: stickyFooterAdEpic', logger),
  )

// audioPlayerEpic :: Epic -> _
export const audioPlayerEpic = (action$, state$, { window, logger }) =>
  action$.pipe(
    ofType(ROUTE_FOUND),
    withLatestFrom(state$),
    filter(([ _, state ]) =>
      state.adnimation.scriptImported
      && isArticleRoute(state.router.activeRoute.name)
      && state.router.matchesCounter > 1
      && typeof window?.adn?.injectTrinityAudioPlayer === 'function',
    ),
    tap(() => window.adn.injectTrinityAudioPlayer()),
    ignoreElements(),
    logObservableError('adnimation :: audioPlayerEpic', logger),
  )

// adTagEpic :: Epic -> _
export const adTagEpic = (action$, state$, { document, logger }) =>
  action$.pipe(
    ofType(ARTICLE_RECEIVED, ROUTE_FOUND),
    withLatestFrom(state$),
    tap(([ _, state ]) => {
      const adTag = isArticleRoute(state.router.activeRoute.name)
        ? state.article?.content?.adTag ?? null
        : null

      let meta = document.querySelector('[name="ad-tag-brand"]')

      if (adTag) {
        if (!meta) {
          meta = document.createElement('meta')
          meta.name = 'ad-tag-brand'
          document.head.appendChild(meta)
        }
        meta.content = adTag
      } else if (meta) {
        meta.remove()
      }
    }),
    ignoreElements(),
    logObservableError('adnimation :: adTagEpic', logger),
  )

export default combineEpics(
  importScriptEpic,
  syncGoogletagGuardEpic,
  toggleOnInterstitialAdEpic,
  toggleOffInterstitialAdEpic,
  stickyFooterAdEpic,
  audioPlayerEpic,
  adTagEpic,
)
